/**
 * Routes and guards.
 *
 * The guard implements the one rule that shapes this whole app: in the
 * platform realm MFA is mandatory (CLAUDE.md §6), and an admin who has not
 * enrolled is confined to the enrolment screen until they have. The server
 * enforces it too — every other endpoint answers `403 ERR_MFA_REQUIRED` — but
 * an operator should meet that as a guided step, not as a screen full of
 * failed requests.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { t } from '@/i18n'
import { geotaxRoutes } from '@/modules/geotax/routes'
import { useAuthStore } from '@/stores/auth'

/**
 * Titles and crumbs are stored as catalogue *keys*, not as prose: route
 * records are built once at module load, long before a locale switch, so a
 * literal here would freeze the document title in whatever language the app
 * booted in.
 */

const routes: RouteRecordRaw[] = [
  // C0: geography master, country-pack editor with version diff, dual-control
  // publish, and the tenant adoption dashboard.
  ...geotaxRoutes,
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, layout: 'bare', titleKey: 'routes.login' },
  },
  {
    // Fixed by the server: `internal/adminapi/admins.go` mails
    // `<AdminBaseURL>/accept-invite?token=…`. Renaming this path breaks every
    // invitation already in someone's inbox.
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/views/AcceptInviteView.vue'),
    meta: { public: true, layout: 'bare', titleKey: 'routes.acceptInvite' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true, layout: 'bare', titleKey: 'routes.forgotPassword' },
  },
  {
    // Likewise fixed: `internal/adminapi/auth.go` mails
    // `<AdminBaseURL>/reset-password?token=…`.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true, layout: 'bare', titleKey: 'routes.resetPassword' },
  },
  {
    path: '/mfa/enrol',
    name: 'mfa-enrol',
    component: () => import('@/views/MfaEnrolView.vue'),
    meta: { layout: 'bare', allowWithoutMfa: true, titleKey: 'routes.mfaEnrol' },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    // No crumbs: the breadcrumb's own root *is* the platform dashboard, and
    // repeating it would render "Platform / Platform".
    meta: { titleKey: 'routes.dashboard' },
  },
  {
    path: '/tenants',
    name: 'tenants',
    component: () => import('@/views/TenantsView.vue'),
    meta: { titleKey: 'routes.tenants', breadcrumb: [{ labelKey: 'routes.tenants' }] },
  },
  {
    path: '/tenants/new',
    name: 'tenant-create',
    component: () => import('@/views/TenantCreateView.vue'),
    meta: {
      titleKey: 'routes.tenantCreate',
      breadcrumb: [
        { labelKey: 'routes.tenants', to: '/tenants' },
        { labelKey: 'routes.tenantCreateCrumb' },
      ],
    },
  },
  {
    path: '/tenants/:id',
    name: 'tenant-detail',
    component: () => import('@/views/TenantDetailView.vue'),
    props: true,
    meta: { titleKey: 'routes.tenantDetail', breadcrumb: [{ labelKey: 'routes.tenants', to: '/tenants' }] },
  },
  {
    path: '/plans',
    name: 'plans',
    component: () => import('@/views/PlansView.vue'),
    meta: { titleKey: 'routes.plans', breadcrumb: [{ labelKey: 'routes.plans' }] },
  },
  {
    path: '/billing',
    redirect: '/billing/subscriptions',
  },
  {
    path: '/billing/subscriptions',
    name: 'billing-subscriptions',
    component: () => import('@/modules/billing/SubscriptionsView.vue'),
    meta: {
      titleKey: 'routes.subscriptions',
      breadcrumb: [
        { labelKey: 'routes.billing', to: '/billing/subscriptions' },
        { labelKey: 'routes.subscriptions' },
      ],
    },
  },
  {
    // Routed on the tenant: the contract has no `GET /subscriptions/:id`, and a
    // tenant holds at most one subscription (see modules/billing/api.ts).
    path: '/billing/subscriptions/:tenantId',
    name: 'billing-subscription',
    component: () => import('@/modules/billing/SubscriptionDetailView.vue'),
    props: true,
    meta: {
      titleKey: 'routes.subscription',
      breadcrumb: [
        { labelKey: 'routes.billing', to: '/billing/subscriptions' },
        { labelKey: 'routes.subscriptions', to: '/billing/subscriptions' },
      ],
    },
  },
  {
    path: '/billing/invoices',
    name: 'billing-invoices',
    component: () => import('@/modules/billing/InvoicesView.vue'),
    meta: {
      titleKey: 'routes.invoices',
      breadcrumb: [
        { labelKey: 'routes.billing', to: '/billing/subscriptions' },
        { labelKey: 'routes.invoices' },
      ],
    },
  },
  {
    path: '/billing/invoices/:id',
    name: 'billing-invoice',
    component: () => import('@/modules/billing/InvoiceDetailView.vue'),
    props: true,
    meta: {
      titleKey: 'routes.invoice',
      breadcrumb: [
        { labelKey: 'routes.billing', to: '/billing/subscriptions' },
        { labelKey: 'routes.invoices', to: '/billing/invoices' },
      ],
    },
  },
  {
    path: '/billing/payments',
    name: 'billing-payments',
    component: () => import('@/modules/billing/PaymentsView.vue'),
    meta: {
      titleKey: 'routes.payments',
      breadcrumb: [
        { labelKey: 'routes.billing', to: '/billing/subscriptions' },
        { labelKey: 'routes.payments' },
      ],
    },
  },
  {
    path: '/admins',
    name: 'admins',
    component: () => import('@/views/AdminsView.vue'),
    meta: { titleKey: 'routes.admins', breadcrumb: [{ labelKey: 'routes.admins' }] },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { titleKey: 'routes.notFound' },
  },
]

export function createAppRouter(): ReturnType<typeof createRouter> {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  })

  router.beforeEach(async (to) => {
    const auth = useAuthStore()
    if (!auth.ready) await auth.loadProfile()

    const isPublic = to.meta['public'] === true

    if (!auth.isAuthenticated) {
      if (isPublic) return true
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    if (isPublic) return { name: 'dashboard' }

    if (auth.needsMfaEnrolment && to.meta['allowWithoutMfa'] !== true) {
      return { name: 'mfa-enrol' }
    }
    if (!auth.needsMfaEnrolment && to.name === 'mfa-enrol') {
      return { name: 'dashboard' }
    }
    return true
  })

  router.afterEach((to) => {
    const key = typeof to.meta['titleKey'] === 'string' ? to.meta['titleKey'] : ''
    document.title =
      key === '' ? t('routes.appName') : t('routes.documentTitle', { title: t(key) })
  })

  return router
}
